/**
 * Plan Restriction Validator
 * 
 * Validates user actions against their subscription plan limits
 */

/**
 * Validates if the selected floor count is within plan limits
 * @param {number} floorsCount - Number of floors to validate
 * @param {object} planLimits - Plan limits object
 * @returns {object} - { valid: boolean, message: string }
 */
export const validateFloorCount = (floorsCount, planLimits) => {
    if (!planLimits) {
        return { valid: true, message: '' };
    }

    const maxFloors = planLimits.max_floors !== undefined && planLimits.max_floors !== null
        ? planLimits.max_floors
        : (planLimits.max_floor !== undefined && planLimits.max_floor !== null
            ? planLimits.max_floor
            : null);

    if (maxFloors === null || maxFloors === undefined) {
        return { valid: true, message: '' };
    }

    // max_floor represents the maximum floor index (0-based)
    // So max_floor: 0 means ground floor only (1 floor)
    // max_floor: 1 means ground + first floor (2 floors)
    const maxFloorsAllowed = maxFloors + 1;

    if (floorsCount > maxFloorsAllowed) {
        return {
            valid: false,
            message: `Your plan allows a maximum of ${maxFloorsAllowed} floor${maxFloorsAllowed > 1 ? 's' : ''}. Please upgrade your plan to create projects with more floors.`
        };
    }

    return { valid: true, message: '' };
};

/**
 * Validates if the selected building type is allowed by the plan
 * @param {string} buildingType - Building type to validate
 * @param {object} planLimits - Plan limits object
 * @returns {object} - { valid: boolean, message: string }
 */
export const validateBuildingType = (buildingType, planLimits) => {
    if (!planLimits || !buildingType) {
        return { valid: true, message: '' };
    }

    const allowedTypes = planLimits.allowed_building_types || planLimits.building_types || [];

    // Empty array means all types are allowed
    if (allowedTypes.length === 0) {
        return { valid: true, message: '' };
    }

    const isAllowed = allowedTypes.includes(buildingType);

    if (!isAllowed) {
        return {
            valid: false,
            message: `Your plan does not support ${buildingType} building type. Allowed types: ${allowedTypes.join(', ')}`
        };
    }

    return { valid: true, message: '' };
};

/**
 * Validates if user can create a new project based on estimation limits
 * @param {object} subscription - User's subscription object
 * @param {object} planLimits - Plan limits object
 * @returns {object} - { valid: boolean, message: string }
 * 
 * Note: This function validates per-project estimation limits (plan.limits.estimations).
 * Subscription available_count is for project creation limits, NOT estimation limits.
 * The backend validates actual estimation usage when estimations are run.
 */
export const validateEstimationLimit = (subscription, planLimits) => {
    if (!planLimits) {
        return { valid: true, message: '' };
    }

    const maxEstimations = planLimits.max_estimations !== undefined && planLimits.max_estimations !== null
        ? planLimits.max_estimations
        : (planLimits.estimations !== undefined && planLimits.estimations !== null
            ? planLimits.estimations
            : null);

    // null means unlimited estimations
    if (maxEstimations === null || maxEstimations === undefined) {
        return { valid: true, message: '' };
    }

    // For plans with defined estimation limits (e.g., FREE plan with 3 estimations),
    // the actual validation happens on the backend when estimations are executed.
    // Frontend only needs to check if the limit is explicitly set to 0.
    if (maxEstimations <= 0) {
        return {
            valid: false,
            message: `Your plan does not allow estimations. Please upgrade your plan.`
        };
    }

    return { valid: true, message: '' };
};

/**
 * Validates if FREE plan user can create a new project
 * @param {string} planCode - Plan code (e.g., "FREE")
 * @param {number} existingProjectsCount - Number of existing projects
 * @returns {object} - { valid: boolean, message: string }
 */
export const validateFreeProjectLimit = (planCode, existingProjectsCount) => {
    if (!planCode || planCode.toUpperCase() !== 'FREE') {
        return { valid: true, message: '' };
    }

    if (existingProjectsCount > 0) {
        return {
            valid: false,
            message: 'FREE plan users can only create one project. Please upgrade your plan to create more projects.'
        };
    }

    return { valid: true, message: '' };
};

/**
 * Gets the maximum allowed floors based on plan limits
 * @param {object} planLimits - Plan limits object
 * @returns {number} - Maximum floors allowed (defaults to 10 if unlimited)
 */
export const getMaxFloorsAllowed = (planLimits) => {
    if (!planLimits) {
        return 10; // Default max if no plan limits
    }

    const maxFloors = planLimits.max_floors !== undefined && planLimits.max_floors !== null
        ? planLimits.max_floors
        : (planLimits.max_floor !== undefined && planLimits.max_floor !== null
            ? planLimits.max_floor
            : null);

    if (maxFloors === null || maxFloors === undefined) {
        return 10; // Default max for unlimited plans
    }

    return maxFloors + 1; // Convert 0-based index to count
};

/**
 * Gets the allowed building types based on plan limits
 * @param {object} planLimits - Plan limits object
 * @returns {array} - Array of allowed building types
 */
export const getAllowedBuildingTypes = (planLimits) => {
    if (!planLimits) {
        return ['INDEPENDENT', 'APARTMENT', 'COMMERCIAL', 'INDUSTRIAL'];
    }

    const allowedTypes = planLimits.allowed_building_types || planLimits.building_types || [];

    // Empty array means all types are allowed
    if (allowedTypes.length === 0) {
        return ['INDEPENDENT', 'APARTMENT', 'COMMERCIAL', 'INDUSTRIAL'];
    }

    return allowedTypes;
};

/**
 * Validates all restrictions before project creation
 * @param {object} params - Validation parameters
 * @returns {object} - { valid: boolean, errors: array }
 */
export const validateProjectCreation = (params) => {
    const {
        floorsCount,
        buildingType,
        planLimits,
        subscription,
        planCode,
        existingProjectsCount
    } = params;

    const errors = [];

    // Validate floor count
    const floorValidation = validateFloorCount(floorsCount, planLimits);
    if (!floorValidation.valid) {
        errors.push(floorValidation.message);
    }

    // Validate building type
    const buildingTypeValidation = validateBuildingType(buildingType, planLimits);
    if (!buildingTypeValidation.valid) {
        errors.push(buildingTypeValidation.message);
    }

    // Validate estimation limit
    const estimationValidation = validateEstimationLimit(subscription, planLimits);
    if (!estimationValidation.valid) {
        errors.push(estimationValidation.message);
    }

    // Validate FREE plan project limit
    const freeProjectValidation = validateFreeProjectLimit(planCode, existingProjectsCount);
    if (!freeProjectValidation.valid) {
        errors.push(freeProjectValidation.message);
    }

    return {
        valid: errors.length === 0,
        errors
    };
};
